import React, {useCallback, useState, useEffect, useRef} from 'react';
import {promiseDebounce} from '../../../../_legacy/library/utils/debounce';
import {SearchAction, Search as SearchType, SearchResult} from 'priority-web-sdk/enums';
import {
    Direction,
    getFocusedIndex,
    showSearchQueryInResults,
    calculateInputDirection
} from '../utils/search';
import {
    getIsDesc,
    getNextIsDesc,
    getNextSortBy,
    getSortBy
} from '../../../../_legacy/FieldSearch/utils/sort';
import {objectToArray} from '../../../../_legacy/library/utils/objects';
import {keys, NAVIGATION_KEYS} from '../../../../constants/KeyCodes';
import {sortResults} from '../../../../_legacy/library/utils/search';

const PAGE_SIZE = 5;

const initState = {
    _results: [],
    results: [],
    isSearchable: false,
    isScrollable: false,
    isSearching: false,
    isNoResults: false,
    searchQuery: '',
    isZoom: false,
    isMulti: false,
    isLoading: true,
    focusedIndex: -1,
    direction: 'auto' as Direction,
    stackCursor: '0',
    searchStart: '0'
};

export interface SearchState {
    _results: SearchResult[];
    results: SearchResult[];
    title1?: string;
    title2?: string;
    searchQuery: string;
    searchByTitle?: string;
    ignorecase?: boolean;
    searchFromBeginning?: boolean;
    isSearchable: boolean;
    isScrollable: boolean;
    isLoading: boolean;
    isSearching: boolean;
    isNoResults: boolean;
    isZoom: boolean;
    isMulti: boolean;
    sortBy?: 'string1' | 'string2';
    isDesc?: boolean;
    direction: Direction;
    chooseid?: string;
    searchInputValue?: string;
    focusedIndex: number;
    stackCursor?: string;
    searchStart: string;
    message?: string;
}

export interface SearchOptions {
    showWildCardOptions: boolean;
    isForceMulti: boolean;
}

type OnSearchAction = (
    action: SearchAction,
    searchQuery?: string,
    stackCursor?: string,
    searchStart?: string
) => Promise<SearchType>;
type OnSearchOpen = (isForceSearch?: boolean, searchQuery?: string) => Promise<SearchType>;
type OnScroll = (
    page: number,
    searchQuery: string,
    stackCursor?: string,
    searchStart?: string
) => Promise<SearchType>;

interface SearchActions {
    onSearchOpen: OnSearchOpen;
    onSearchAction: OnSearchAction;
    onScroll: OnScroll;
}

export interface SearchHandlers {
    handleSearchOpen(
        searchQuery?: string,
        isForceSearch?: boolean,
        isFilterChooseResults?: boolean
    ): Promise<any>;
    handleSearchChange(searchQuery?: string): Promise<any>;
    handleScroll(): Promise<any>;
    handleSortByChange(sortBy: string): void;
    handleNavigationKeys(event: KeyboardEvent): void;
    handleIgnoreCaseChange(): Promise<any>;
    handleSearchFromBeginningChange(): Promise<any>;
    handleSearchByChange(searchQuery?: string): Promise<any>;
    handleSearchQueryChange(searchQuery?: string): void;
    dispatchState: React.Dispatch<React.SetStateAction<SearchState>>;
}

export function serializeSearch(
    result: SearchType = null,
    searchQuery: string,
    showWildCardOptions: boolean,
    isForceMulti: boolean,
    isFilterChooseResults: boolean = false
): Partial<SearchState> {
    if (result && (result.ChooseLine || result.SearchLine)) {
        const ignorecase = result.ChooseLine ? true : result.ignorecase === '1';
        const searchFromBeginning = result.SearchLine ? result.start === '1' : !result.title2;
        const direction = calculateInputDirection(result);

        let searchResults;
        if (result.ChooseLine) {
            searchResults = objectToArray(result.ChooseLine).map(({str1, str2, retval}) => ({
                string1: str1,
                string2: str2,
                retval
            }));
        } else {
            searchResults = objectToArray(result.SearchLine);
        }
        searchResults = searchResults.filter(({retval, string1, string2}) => {
            return retval || (string1 && string1.trim()) || (string2 && string2.trim());
        });
        const isSearchable = !!result.SearchLine;
        const searchInputValue = result.value || '';
        const isMulti = isForceMulti || result.multi === '1' || result.ismulti === '1';
        if (
            showWildCardOptions &&
            isMulti &&
            isSearchable &&
            showSearchQueryInResults(searchQuery)
        ) {
            searchResults.unshift({string1: searchQuery, retval: searchQuery});
        }
        const results =
            isFilterChooseResults && !isSearchable && result.title2 && searchQuery
                ? filterResults(searchResults, searchQuery)
                : searchResults;
        if (searchResults.length) {
            return {
                results: results,
                _results: searchResults,
                isSearchable,
                isScrollable: result.next === '1',
                searchByTitle: result.cursor,
                searchFromBeginning,
                ignorecase,
                title1: result.title1,
                title2: result.title2,
                chooseid: result.chooseid,
                sortBy: getSortBy(result.sortby),
                isDesc: getIsDesc(result.sortby),
                isMulti,
                searchInputValue,
                direction,
                focusedIndex: getFocusedIndex(results, searchQuery, isSearchable, isMulti),
                stackCursor: result.stack_cursor,
                searchStart: result.stack_start
            };
        } else {
            return {
                results: searchResults,
                _results: searchResults,
                searchByTitle: result.cursor,
                isSearchable,
                isZoom: !!result.zoom,
                ignorecase,
                searchFromBeginning,
                searchInputValue,
                direction,
                stackCursor: result.stack_cursor,
                searchStart: result.stack_start
            };
        }
    }
    return {isNoResults: true, _results: [], isZoom: true};
}

export function filterResults(results, searchQuery) {
    return results.filter(({string1, string2}) => {
        return (
            (string1 && string1.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (string2 && string2.toLowerCase().includes(searchQuery.toLowerCase()))
        );
    });
}

export const useSearch = (
    actions: SearchActions,
    searchQuery: string = '',
    options: SearchOptions = {showWildCardOptions: false, isForceMulti: false}
) => {
    const isScrolling = useRef(false);
    const {onSearchOpen, onSearchAction: onSearch, onScroll} = actions;
    const [state, dispatchState] = useState<SearchState>({
        ...initState,
        searchQuery
    });
    const {showWildCardOptions, isForceMulti} = options;
    const setState = (partialState: Partial<SearchState>) =>
        dispatchState(state => ({...state, ...partialState}));

    const onSearchAction = useCallback(promiseDebounce(onSearch, 400), []);

    /****  OPEN ****/
    const handleSearchOpen = useCallback(
        async (
            searchQuery: string = '',
            isForceSearch: boolean = false,
            isFilterChooseResults = false
        ) => {
            setState({isLoading: true, searchQuery});
            const search = await onSearchOpen(isForceSearch, searchQuery);
            const serializedSearch = serializeSearch(
                search,
                searchQuery,
                showWildCardOptions,
                isForceMulti,
                isFilterChooseResults
            );
            const {results, searchInputValue} = serializedSearch;

            setState({
                isLoading: false,
                ...serializedSearch,
                results,
                searchQuery: searchInputValue || searchQuery,
                message: search.message
            });
        },
        [state, isForceMulti]
    );

    /****  SEARCH CHANGE ****/
    const handleSearchChange = useCallback(
        async (searchQuery: string) => {
            const {isSearchable, isMulti, title2} = state;
            if (isSearchable) {
                setState({isSearching: true, searchQuery});
                const search = await onSearchAction(
                    SearchAction.TextChange,
                    searchQuery,
                    state.stackCursor,
                    state.searchStart
                );
                setState({
                    isSearching: false,
                    ...serializeSearch(search, searchQuery, showWildCardOptions, isForceMulti)
                });
            } else if (title2) {
                const {_results} = state;
                const results = filterResults(_results, searchQuery);
                setState({
                    results,
                    searchQuery,
                    focusedIndex: getFocusedIndex(results, searchQuery, isSearchable, isMulti)
                });
            } else {
                const {results} = state;
                setState({
                    searchQuery,
                    focusedIndex: getFocusedIndex(results, searchQuery, isSearchable, isMulti)
                });
            }
        },
        [state, isForceMulti]
    );

    /****  SCROLL ****/
    const pageIndex = useRef(0);

    useEffect(() => {
        // reset page index
        pageIndex.current = 0;
    }, [
        state.isLoading,
        state.searchByTitle,
        state.searchFromBeginning,
        state.ignorecase,
        state.searchQuery,
        state.sortBy
    ]);

    const handleScroll = useCallback(
        async (nextFocusedIndex = state.focusedIndex) => {
            if (state.isScrollable && !isScrolling.current) {
                try {
                    isScrolling.current = true;
                    const nextPage = pageIndex.current + 1;
                    const search = await onScroll(
                        nextPage,
                        state.searchQuery,
                        state.stackCursor,
                        state.searchStart
                    );

                    pageIndex.current = nextPage;
                    const serializedSearch = serializeSearch(
                        search,
                        state.searchQuery,
                        false,
                        isForceMulti
                    );
                    const nextResults = serializedSearch.results;
                    const {results, _results} = state;
                    setState({
                        ...serializedSearch,
                        focusedIndex: nextFocusedIndex,
                        _results: _results.concat(nextResults),
                        results: results.concat(nextResults)
                    });
                } catch (e) {
                    console.log('OnScroll: canceled', e);
                } finally {
                    isScrolling.current = false;
                }
            }
        },
        [state, isForceMulti]
    );

    /****  SEARCH ACTION ****/
    const __handleSearchAction = useCallback(
        async (action: SearchAction, searchQuery: string = state.searchQuery) => {
            setState({isSearching: true});
            const search = await onSearchAction(
                action,
                searchQuery,
                state.stackCursor,
                state.searchStart
            );
            setState({
                searchQuery,
                isSearching: false,
                ...serializeSearch(search, searchQuery, showWildCardOptions, isForceMulti)
            });
        },
        [state, isForceMulti]
    );

    /****  SORT ****/
    const handleSortByChange = useCallback(
        (sortByVal: string) => {
            const {sortBy, isDesc, results, _results, searchQuery, isSearchable, isMulti} = state;
            const nextIsDesc = getNextIsDesc(sortByVal, sortBy, isDesc);
            const nextSortBy = getNextSortBy(sortByVal, sortBy, isDesc);
            const sortedRes = nextSortBy
                ? sortResults(results, nextSortBy, nextIsDesc)
                : [..._results];
            setState({
                results: sortedRes,
                sortBy: nextSortBy,
                isDesc: nextIsDesc,
                focusedIndex: getFocusedIndex(sortedRes, searchQuery, isSearchable, isMulti)
            });
        },
        [state]
    );

    const handleIgnoreCaseChange = () => {
        if (!state.isSearching) {
            dispatchState(state => ({...state, ...{ignorecase: !state.ignorecase}}));
            __handleSearchAction(SearchAction.IgnoreCaseChanged);
        }
    };

    const handleSearchFromBeginningChange = () => {
        if (!state.isSearching) {
            dispatchState(state => ({
                ...state,
                ...{searchFromBeginning: !state.searchFromBeginning}
            }));
            __handleSearchAction(SearchAction.StartChange);
        }
    };

    const handleSearchByChange = (searchQuery: string = state.searchQuery) => {
        __handleSearchAction(SearchAction.TypeChange, searchQuery);
    };

    const handleNavigationKeys = (event: KeyboardEvent) => {
        if (NAVIGATION_KEYS.includes(event.key)) {
            event.stopPropagation();
            const {focusedIndex, results, isMulti, isScrollable} = state;
            const topFocusedIndex = isMulti ? -1 : 0;
            const bottomFocusedIndex = results.length - 1;
            let nextFocusedIndex = focusedIndex;
            switch (event.key) {
                case keys.ARROW_UP:
                    event.preventDefault();
                    if (focusedIndex === topFocusedIndex || focusedIndex === -1) {
                        nextFocusedIndex = bottomFocusedIndex;
                    } else {
                        nextFocusedIndex = focusedIndex - 1;
                    }
                    break;
                case keys.ARROW_DOWN:
                    event.preventDefault();
                    if (focusedIndex === bottomFocusedIndex && !isScrollable) {
                        nextFocusedIndex = topFocusedIndex;
                    } else {
                        nextFocusedIndex = focusedIndex + 1;
                    }
                    break;
                case keys.PAGE_UP:
                    event.preventDefault();
                    if (focusedIndex - PAGE_SIZE > topFocusedIndex) {
                        nextFocusedIndex = focusedIndex - PAGE_SIZE;
                    } else {
                        nextFocusedIndex = 0;
                    }
                    break;
                case keys.PAGE_DOWN:
                    event.preventDefault();
                    if (focusedIndex + PAGE_SIZE >= bottomFocusedIndex && !isScrollable) {
                        nextFocusedIndex = bottomFocusedIndex;
                    } else {
                        nextFocusedIndex = focusedIndex + PAGE_SIZE;
                    }
                    break;
                case keys.HOME:
                    if (!state.isSearchable) {
                        event.preventDefault();
                        nextFocusedIndex = topFocusedIndex;
                    }
                    break;
                case keys.END:
                    if (!state.isSearchable) {
                        event.preventDefault();
                        nextFocusedIndex = bottomFocusedIndex;
                    }
                    break;
            }
            if (isScrollable && nextFocusedIndex >= bottomFocusedIndex) {
                handleScroll(nextFocusedIndex);
            } else {
                setState({focusedIndex: nextFocusedIndex});
            }
        }
    };

    const handleSearchQueryChange = (searchQuery: string = '') => {
        setState({searchQuery});
    };

    const handlers = {
        handleSearchOpen,
        handleSearchChange,
        handleScroll,
        handleSortByChange,
        handleIgnoreCaseChange,
        handleSearchByChange,
        handleSearchFromBeginningChange,
        handleNavigationKeys,
        handleSearchQueryChange,
        dispatchState
    };

    return [state, handlers] as [SearchState, SearchHandlers];
};
